import { useState, useRef } from 'react'
import { ChevronRight, ChevronUp } from 'lucide-react'

interface FAQItem {
  key: string
  title: string
  content: string
}

const faqData: FAQItem[] = [
  {
    key: 'subscription',
    title: '🔁 What is AI Mining Subscription?',
    content: `AI Mining Subscription is a token airdrop program by PIN AI for community users and Binance KYC users. By activating a Subscription using your Binance non-custodial wallet, you can participate in token mining within the PIN AI Native App. Activation requires on-chain confirmation and consumes BNB as gas.`
  },
  {
    key: 'benefits',
    title: '💸 What are the benefits of paid subscription?',
    content: `Get access to both Free and Paid Pools for double the earning potential, and enjoy up to 10× AI (combining both public pool and premium pool) Boost Multiplier for significantly higher mining efficiency. Receive $30 worth of AI Credits every month and gain exclusive access to advanced AI features and early access to new tools and experiments.`
  },
  {
    key: 'rewards',
    title: '📦 How are my token rewards calculated?',
    content: `Each week, 50% of tokens go to the Public Pool (for all users), and 50% to the Premium Pool (for paying users).
All subscribers can earn from the Public Pool, with multipliers based on subscription level: 1× (Free), 3× (Premium), 5× (Pro). You also get a 1% bonus if invited.
Paying users enjoy double rewards by also sharing the Premium Pool.
Completing weekly tasks and inviting subscribers can further boost your weight and increase your earnings.
More details: guide.pinai.io`
  },
  {
    key: 'claiming',
    title: '📅 When can I claim my tokens?',
    content: `Token release starts gradually 91days after Binance alpha listing. Token vesting period would be 3 months + N week, N is the week that you completed the task.`
  },
  {
    key: 'refunds',
    title: '🔄 Can I refund my subscription if I don\'t want it anymore?',
    content: `Yes. If you're not satisfied, you can request a full refund during the 7-day refund window, which begins 10 days before the first token release.
Upon refund, all Paid Pool rewards and the extra portion earned from the AI Boost Multiplier in the Free Pool will be revoked and redistributed to other active paid subscribers.
More details: guide.pinai.io`
  }
]

export function FAQSection() {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    subscription: true // 默认展开第一个FAQ
  })
  
  const faqRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // 滚动到合适位置的辅助函数
  const scrollToOptimalPosition = (faqKey: string) => {
    const faqElement = faqRefs.current[faqKey]
    if (!faqElement) return

    // 延迟执行，等待DOM更新完成
    setTimeout(() => {
      const faqRect = faqElement.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const scrollY = window.scrollY
      
      // 计算FAQ展开后的大概高度（估算）
      const estimatedExpandedHeight = faqRect.height + 200 // 预留200px的内容高度
      
      // 检查是否需要滚动：
      // 1. FAQ底部距离屏幕底部小于100px
      // 2. 或者FAQ顶部已经很靠近屏幕顶部但展开后会超出屏幕
      const bottomSpacing = viewportHeight - faqRect.bottom
      const needsScroll = bottomSpacing < 100 || (faqRect.top < 100 && estimatedExpandedHeight > viewportHeight - faqRect.top)
      
      if (needsScroll) {
        // 计算目标滚动位置：让FAQ标题位于屏幕中上部
        const targetScrollY = scrollY + faqRect.top - Math.max(80, viewportHeight * 0.2)
        
        // 平滑滚动到目标位置
        window.scrollTo({
          top: Math.max(0, targetScrollY),
          behavior: 'smooth'
        })
      }
    }, 150) // 等待展开动画开始
  }

  const toggleFaq = (key: string) => {
    const wasExpanded = expandedItems[key]
    
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
    
    // 如果是展开操作，执行滚动检查
    if (!wasExpanded) {
      scrollToOptimalPosition(key)
    }
  }

  return (
    <div className="bg-white rounded-xl p-2.5 mb-6">
      <h3 className="font-sf-pro font-bold text-[#280D5E] text-[25px] leading-[40px] mb-1.5">
        FAQ
      </h3>
      
      <div className="flex flex-col gap-0">
        {faqData.map((item) => (
          <div 
            key={item.key} 
            className="border-b border-gray-100 last:border-b-0"
            ref={(el) => { faqRefs.current[item.key] = el }}
          >
            <button 
              onClick={() => toggleFaq(item.key)}
              className="w-full flex justify-between items-center py-4"
            >
              <span className="font-inter font-semibold text-black text-sm leading-[22px] text-left">
                {item.title}
              </span>
              <div className="flex items-center gap-1">
                <span className="font-sf-pro font-bold text-[#489EAC] text-sm">
                  {expandedItems[item.key] ? 'Hide' : 'Details'}
                </span>
                {expandedItems[item.key] ? (
                  <ChevronUp className="w-[18px] h-[18px] text-[#489EAC]" />
                ) : (
                  <ChevronRight className="w-[18px] h-[18px] text-[#489EAC]" />
                )}
              </div>
            </button>
            
            {expandedItems[item.key] && (
              <div className="pb-4 px-0">
                <div className="font-sf-pro font-medium text-[#7D73AC] text-[13px] leading-[22px] whitespace-pre-line">
                  {item.content}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
} 